import React from 'react';
import { NewModal } from 'lib/components';
import { Flex } from 'lib/components/styles/layout';
import { Formik, Form, FormikValues } from 'formik';
import { ParagraphSmall } from 'lib/components/styles/typography';
import { Button } from 'react-covideo-common';
import { FormikInputField } from 'lib/components/formik/FormikInputField';
import * as Yup from 'yup';
import { useTheme } from 'styled-components/macro';
import { useUpdateLandingPageDesignMutation } from 'lib/api/superadmin/landingPageDesigns/useUpdateLandingPageDesignMutation';
import { useTranslation } from 'react-i18next';

interface IEditLandingPageDesignModalProps {
  templateId: number;
  title: string;
  closeModalHandler: () => void;
}

const renameValidationSchema = Yup.object({
  title: Yup.string()
    .required('Title is required')
    .test(
      'not-just-spaces',
      'Please enter a valid title',
      value => !value || value.trim().length > 0
    ),
});

export const RenameLandingPageDesignModal = ({
  templateId,
  title,
  closeModalHandler,
}: IEditLandingPageDesignModalProps) => {
  const { t } = useTranslation();
  const { mutateAsync: handleRename, isLoading } =
    useUpdateLandingPageDesignMutation(closeModalHandler);
  const theme = useTheme();
  const onSubmitHandler = async (values: FormikValues) => {
    await handleRename({ templateId, title: values.title.trim() });
  };

  return (
    <NewModal
      headerText={t('Rename Landing Page')}
      closeModal={closeModalHandler}
      style={{
        content: {
          maxHeight: 284,
          width: 592,
          maxWidth: 592,
          padding: '22px 42px',
        },
      }}
    >
      <Formik
        initialValues={{ title: title || '' }}
        onSubmit={onSubmitHandler}
        validationSchema={renameValidationSchema}
        validateOnMount
        enableReinitialize
      >
        {({ handleSubmit, isValid, setFieldValue }) => {
          return (
            <Form onSubmit={handleSubmit}>
              <Flex gap='24px'>
                <ParagraphSmall style={{ color: theme.colors.neutral[80] }}>
                  {t('Please add a new name to your landing page')}
                </ParagraphSmall>
                <FormikInputField
                  label='Name'
                  name='title'
                  type='text'
                  isRequired
                  autoFocus={true}
                  disabled={isLoading}
                  onChange={e => setFieldValue('title', e.target.value)}
                />
                <Flex
                  flexDirection='row'
                  width='100%'
                  justifyContent='flex-end'
                >
                  <Button
                    text={isLoading ? t('Saving...') : t('Save')}
                    disabled={!isValid || isLoading}
                    type='submit'
                  />
                </Flex>
              </Flex>
            </Form>
          );
        }}
      </Formik>
    </NewModal>
  );
};
